// Single source of truth for how dates and numbers render in the admin (design §6: one locale
// format everywhere, no ad-hoc ISO strings, no raw API decimals). RU locale, 24h, no seconds.
//
// Numbers (WP-04): every amount, count and percentage goes through the `fmt*` helpers below, so
// an operator reads «12 845 300,00 ₽», «1 020,00 USDT», «65,7 %», «800 тыс.» on every page.
// MONEY: values stay the API's decimal strings end to end. They are handed to Intl as strings
// (ECMA-402 NumberFormat v3: exact decimal, rounding half away from zero), never through
// `Number()`, and only for display — nothing here changes a stored value.

const DATETIME = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const DATE = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/** The admin's short time units — one spelling on every screen (wave-4 review: the support
 *  queue said «2 дн» next to the backups card's «2 д»): «45 с», «8 мин», «3 ч», «2 д». */
export const TIME_UNIT = { second: 'с', minute: 'мин', hour: 'ч', day: 'д' } as const

export function fmtDateTime(ts: string | null | undefined): string {
  if (!ts) return '—'
  const d = new Date(ts)
  return Number.isNaN(d.getTime()) ? '—' : DATETIME.format(d)
}

export function fmtDate(ts: string | null | undefined): string {
  if (!ts) return '—'
  const d = new Date(ts)
  return Number.isNaN(d.getTime()) ? '—' : DATE.format(d)
}

// ───────────────────────────── numbers ─────────────────────────────

/** What the API sends for a number: a decimal string ("4812344.50"), a JS number (counts), or
 *  nothing. */
export type Num = string | number | null | undefined

/** No-break space: groups digits and binds a unit to its number («1 020,00 USDT»). */
export const NBSP = '\u00a0'
/** The typographic minus. Intl writes a hyphen-minus, which reads as a dash in a column. */
const MINUS = '\u2212'
const EMPTY = '—'

const DECIMAL = /^[-+]?\d+(?:\.\d+)?$/

/** A decimal literal Intl can take as a string, or null for text that is not a number (it is
 *  then shown as it came, never as «не число»). Numbers go through `String()` unless they would
 *  print in exponent form. */
function literal(v: string | number): string | null {
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return null
    const s = String(v)
    return DECIMAL.test(s) ? s : v.toFixed(20).replace(/\.?0+$/, '')
  }
  const s = v.trim()
  return DECIMAL.test(s) ? s : null
}

const cache = new Map<string, Intl.NumberFormat>()

function nf(min: number, max: number, signed: boolean): Intl.NumberFormat {
  const key = `${min}:${max}:${signed}`
  let f = cache.get(key)
  if (!f) {
    // 'exceptZero' adds «+» to a change («+20 ⚡» in the ledger). Not 'negative' for the
    // unsigned case: older WebViews throw on it; a «-0» is cleaned up in fmtDecimal instead.
    f = new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
      signDisplay: signed ? 'exceptZero' : 'auto',
    })
    cache.set(key, f)
  }
  return f
}

export interface DecimalOptions {
  min?: number
  max?: number
  /** a change: «+20» as well as «−20» */
  signed?: boolean
}

/** The digits of `v` with `min`…`max` fraction digits, grouped, «−» for negatives. Text that is
 *  not a number comes back unchanged; null / undefined / '' as «—». */
export function fmtDecimal(v: Num, { min = 0, max = 2, signed = false }: DecimalOptions = {}): string {
  if (v === null || v === undefined || v === '') return EMPTY
  const lit = literal(v)
  if (lit === null) return String(v)
  // ES2022 lib types `format` as number | bigint; the runtime takes the exact string (NumberFormat v3).
  const out = nf(min, max, signed).format(lit as unknown as number)
  // a negative value that rounds to zero prints «0», not «-0»
  if (/^-[0,\s\u00a0]+$/.test(out)) return out.slice(1)
  return out.replace('-', MINUS)
}

/** A count: «128 430». Fractions are rounded away. */
export function fmtInt(v: Num): string {
  return fmtDecimal(v, { min: 0, max: 0 })
}

/** Currency code → what follows the number. Codes not listed are shown as they are. */
const CURRENCY_SIGN: Record<string, string> = { RUB: '₽', USD: '$', EUR: '€', USDT: 'USDT', XTR: 'Stars' }
/** Decimals a currency is shown with by default (Stars are whole). */
const CURRENCY_SCALE: Record<string, number> = { XTR: 0 }

/** Money with its sign: fmtMoney('12845300', 'RUB') → «12 845 300,00 ₽»,
 *  fmtMoney('1020.000000', 'USDT') → «1 020,00 USDT». Without a currency (the unit is in the
 *  column header or label) only the number: «112 804,19». */
export function fmtMoney(
  v: Num,
  cur?: string | null,
  { scale, signed = false, exact }: { scale?: number; signed?: boolean; exact?: number } = {},
): string {
  const code = (cur ?? '').toUpperCase()
  const s = scale ?? CURRENCY_SCALE[code] ?? 2
  // `exact` (fmtMoneyExact): up to that many decimals, the usual `s` at least.
  const n = fmtDecimal(v, { min: s, max: Math.max(s, exact ?? s), signed })
  if (n === EMPTY || !code) return n
  if (literal(v as string | number) === null) return n // not a number: no sign after text
  return `${n}${NBSP}${CURRENCY_SIGN[code] ?? code}`
}

/** Money exactly as stored, for the places where a rounded figure would mislead: the confirm
 *  of a payout or a refund (a dialog has no tooltip) and crypto wallets, where «0,00 BTC» hid a
 *  real balance. The currency's usual decimals at least, then every non-zero digit the API
 *  sent, never rounded: fmtMoneyExact('12.345678', 'USDT') → «12,345678 USDT»,
 *  fmtMoneyExact('1020.000000', 'USDT') → «1 020,00 USDT», fmtMoneyExact('0.00012345', 'BTC')
 *  → «0,00012345 BTC». */
export function fmtMoneyExact(v: Num, cur?: string | null): string {
  const lit = v === null || v === undefined || v === '' ? null : literal(v)
  const frac = lit?.split('.')[1]?.replace(/0+$/, '').length ?? 0
  // Intl takes at most 100 fraction digits (20 in older engines); API decimals carry ≤ 18.
  return fmtMoney(v, cur, { exact: Math.min(frac, 20) })
}

/** USDT for reading: two decimals («1 020,00 USDT»). The API keeps six (NUMERIC(18,6)); show
 *  them with `fmtUsdtFull` in a `title`, so nothing is hidden from the operator. */
export function fmtUsdt(v: Num): string {
  return fmtMoney(v, 'USDT')
}

/** USDT with all six decimals of the column: «18 204,551200 USDT» (a `title` tooltip). */
export function fmtUsdtFull(v: Num): string {
  return fmtMoney(v, 'USDT', { scale: 6 })
}

/** The credit unit (glossary D8) with VS15 (U+FE0E): the text presentation of ⚡, so it takes
 *  the ink colour of the number instead of the yellow emoji, which is outside the palette
 *  (WP-12). Where no font has a text ⚡ the emoji stays as the fallback. */
export const BOLT = '\u26a1\ufe0e'

/** A text the server wrote — an error, a validation message, a check result («Не больше
 *  10 000 ⚡ за место») — through the same unit as the rest of the admin: every bare ⚡, or ⚡ with
 *  the emoji selector U+FE0F, becomes `BOLT` (the ink of its line, not the yellow emoji outside
 *  the palette), and a number keeps its ⚡ on the same line (NBSP, §3.3). Idempotent: a `BOLT`
 *  stays one. `ApiError` passes every server message through it, and so does the toast. */
export function units(text: string): string {
  return text.replace(/(\d) (?=\u26a1)/gu, `$1${NBSP}`).replace(/\u26a1\ufe0f?(?!\ufe0e)/gu, BOLT)
}

/** Credits (glossary D8): no decimals when whole, at most two otherwise, then ⚡ —
 *  «4 812 344,5 ⚡», «500 ⚡». `unit: false` when the label already names the unit. */
export function fmtCredits(v: Num, { unit = true, signed = false }: { unit?: boolean; signed?: boolean } = {}): string {
  const n = fmtDecimal(v, { min: 0, max: 2, signed })
  if (!unit || n === EMPTY || literal(v as string | number) === null) return n
  return `${n}${NBSP}${BOLT}`
}

/** A percentage given in percent («65.7» → «65,7 %»): `digits` decimals always shown, up to
 *  `max` when given («15 %», «12,5 %» with digits 0, max 2). */
export function fmtPct(v: Num, { digits = 1, max }: { digits?: number; max?: number } = {}): string {
  const n = fmtDecimal(v, { min: digits, max: Math.max(digits, max ?? digits) })
  if (n === EMPTY || literal(v as string | number) === null) return n
  return `${n}${NBSP}%`
}

/** An exchange rate in rubles: «92,41 ₽». */
export function fmtRate(v: Num): string {
  return fmtMoney(v, 'RUB', { scale: 2 })
}

const COMPACT = new Intl.NumberFormat('ru-RU', { notation: 'compact', maximumFractionDigits: 1 })

/** Chart ticks: «800 тыс.», «1,3 млн», «4,5 тыс.». Ticks are plotted numbers already, so this
 *  one takes a number. */
export function fmtCompact(v: number): string {
  if (!Number.isFinite(v)) return EMPTY
  return COMPACT.format(v).replace('-', MINUS)
}
